'use client';

import { estimateMidpoint, type HomeValueEstimate } from '@/lib/home-value/types';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export interface EstimateResultCardProps {
  estimate: HomeValueEstimate;
  submitting: boolean;
  onGetOffer: () => void;
  onReset: () => void;
}

export function EstimateResultCard({
  estimate,
  submitting,
  onGetOffer,
  onReset,
}: EstimateResultCardProps) {
  return (
    <div className="rounded-3xl bg-canvas-deep p-6 sm:p-8" data-testid="valuation-result">
      <p className="text-ink-muted text-body-sm">Estimated value of</p>
      <p className="mt-1 font-medium text-ink text-body-md">{estimate.formattedAddress}</p>

      <p className="mt-6 font-medium leading-none tracking-tight text-ink text-display-xs sm:text-display-sm">
        {currencyFormatter.format(estimateMidpoint(estimate))}
      </p>
      <p className="mt-3 text-ink-muted text-body-md">
        Estimated range {currencyFormatter.format(estimate.lowEstimate)} -{' '}
        {currencyFormatter.format(estimate.highEstimate)}
      </p>
      {estimate.compCount > 0 && (
        <p className="mt-2 text-ink-faint text-body-sm">
          Based on {estimate.compCount} comparable homes nearby
        </p>
      )}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={onGetOffer}
          disabled={submitting}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink px-6 py-3 font-medium text-ink-light transition hover:bg-canvas-dark disabled:opacity-60 text-body-sm"
        >
          {submitting ? 'Starting…' : 'Get my cash offer'}
          {!submitting && <span aria-hidden>→</span>}
        </button>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex flex-1 items-center justify-center rounded-full border border-line-strong px-6 py-3 font-medium text-ink transition hover:bg-state-hover text-body-sm"
        >
          Try another address
        </button>
      </div>

      <p className="mt-5 text-ink-faint text-body-sm">
        This estimate is for informational purposes only and is not an appraisal or an offer to
        purchase.
      </p>
    </div>
  );
}
